import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getSessionAnalysis } from "../graphql/queries";
import { updateSessionAnalysis } from "../graphql/mutations";
import { usePCCSession } from "../components/hooks/usePCCSession";
import SignNoteButton from "./SignNoteButton";
import AddAddendum from "./AddAddendum";
import { post } from "aws-amplify/api";
// 🆕 import the type-ahead
import PatientSelectTypeahead from "../components/PatientSelectTypeahead";
import SendToEBridgeButton from "./SendToEBridgeButton";

const SYSTEM_LABELS = {
  constitutional: "Constitutional",
  skin: "Skin",
  ent: "ENT",
  cardiovascular: "Cardiovascular",
  respiratory: "Respiratory",
  gastrointestinal: "GI",
  genitourinary: "GU",
  musculoskeletal: "Musculoskeletal",
  neurological: "Neuro",
  psychiatric: "Psych",
  endocrine: "Endocrine",
  lymphatic: "Lymphatic",
  allergicAndImmunologic: "Allergy/Immunology",
};

const DEFAULT_REVIEW_OF_SYSTEMS = {
  constitutional: "No fever, chills, weight loss, or fatigue reported.",
  skin: "No rash, itching, or skin lesions noted.",
  ent: "No ear pain, nasal congestion, or sore throat.",
  cardiovascular: "Denies chest pain, palpitations, or edema.",
  respiratory: "No shortness of breath, wheezing, or cough.",
  gastrointestinal: "No nausea, vomiting, diarrhea, or abdominal pain.",
  genitourinary: "No dysuria, hematuria, or urinary frequency.",
  musculoskeletal: "No joint pain, stiffness, or swelling.",
  neurological: "No headaches, dizziness, numbness, or weakness.",
  psychiatric: "No anxiety, depression, or mood changes reported.",
  endocrine: "No polyuria, polydipsia, or heat/cold intolerance.",
  lymphatic: "No swollen lymph nodes or unusual bleeding/bruising.",
  allergicAndImmunologic: "No known allergies or recent infections reported.",
};

const PLACE_OF_SERVICE_OPTIONS = [
  "Office",
  "Assisted Living",
  "SNF",
  "Home",
  "Outpatient",
  "In patient",
  "ER",
];

const client = generateClient();

// 🆕 Simple 20ms gap helper for PCC calls from this component
const MIN_PCC_GAP_MS = 20;
let lastPccCallTime = 0;

async function ensureMinPccGap() {
  const now = Date.now();
  const elapsed = now - lastPccCallTime;
  const wait = Math.max(0, MIN_PCC_GAP_MS - elapsed);
  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
  lastPccCallTime = Date.now();
}

export default function SessionResultView({ sessionId, createdAt }) {
  const navigate = useNavigate();
  const {
    pccUserId,
    orgUuid,
    token,

    // Back-compat data (strings & raw facilities)
    noteTypes: hookNoteTypes,
    facilities: hookFacilities,

    // Preferred UI-friendly options
    noteTypeOptions: hookNoteTypeOptions,
    facilitiesOptions: hookFacilitiesOptions,

    // Soft default (do not force)
    defaultFacId,

    // 🆕 PCC quota flag from usePCCSession
    quotaExceeded,
  } = usePCCSession();
  const hasPCCSession = !!(pccUserId && orgUuid && token);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editedResult, setEditedResult] = useState({});

  // Local options actually rendered by selects
  const [facilitiesOptions, setFacilitiesOptions] = useState([]);
  const [noteTypeOptions, setNoteTypeOptions] = useState([]);

  // Controlled selections (always strings for <select>)
  const [selectedFacilityId, setSelectedFacilityId] = useState("");
  const [selectedNoteTypeId, setSelectedNoteTypeId] = useState("");

  // 🆕 show PCC→DB sync status
  const [syncingPatients, setSyncingPatients] = useState(false);
  const [loadingPatientClinicalData, setLoadingPatientClinicalData] = useState(false);
  const [patientClinicalDataError, setPatientClinicalDataError] = useState("");
  const [patientClinicalDataWarning, setPatientClinicalDataWarning] = useState("");
  const [selectedPlaceOfService, setSelectedPlaceOfService] = useState("");
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const activePatientFetchRef = useRef(0);

  // ---------- Session-scoped storage key to avoid cross-session collisions ----------
  const PCC_ID_KEY = `pcc_progressNoteId:${sessionId}`;

  // ------------ Resolve PCC Note Id (schema field → analysisResult JSON → sessionStorage[session-scoped]) ------------
  const resolvedPccNoteIdFromSchema = result?.pccNoteId || null;
  const resolvedPccNoteIdFromJson = editedResult?.pccProgressNoteId || null;
  const resolvedPccNoteIdFromStorage =
    (typeof window !== "undefined" ? sessionStorage.getItem(PCC_ID_KEY) : null) || null;

  // Only trust storage if schema/JSON are empty
  const resolvedPccNoteId =
    resolvedPccNoteIdFromSchema || resolvedPccNoteIdFromJson || resolvedPccNoteIdFromStorage || null;

  const hasPushedToPCC = !!resolvedPccNoteId;
  const isBehaviorTherapy = editedResult?.noteTemplate === "BEHAVIOR_THERAPY";

  // Build options from sessionStorage (preferred) or fall back to your existing caches
  useEffect(() => {
    // One-time cleanup for old global key from pre-scoped versions
    try {
      sessionStorage.removeItem("pcc_progressNoteId");
    } catch {}

    // Preferred: options pushed by Lambda → PCCCallbackHandler
    const cachedFacilitiesOptions = JSON.parse(sessionStorage.getItem("pcc_facilitiesOptions") || "[]");
    const cachedNoteTypeOptions = JSON.parse(sessionStorage.getItem("pcc_noteTypeOptions") || "[]");

    // Back-compat: raw arrays
    const cachedFacilities = JSON.parse(sessionStorage.getItem("pcc_facilities") || "[]");
    const cachedNoteTypes = JSON.parse(sessionStorage.getItem("pcc_noteTypes") || "[]");

    // Facilities
    let facOpts =
      Array.isArray(cachedFacilitiesOptions) && cachedFacilitiesOptions.length
        ? cachedFacilitiesOptions
        : Array.isArray(hookFacilitiesOptions) && hookFacilitiesOptions.length
        ? hookFacilitiesOptions
        : Array.isArray(cachedFacilities) && cachedFacilities.length
        ? cachedFacilities.map((f) => ({
            id: f.facId ?? f.id ?? f.facilityId,
            value: String(f.facId ?? f.id ?? f.facilityId),
            label:
              (f.facilityName || f.name || f.facName || f.description || `Facility ${f.facId ?? f.id ?? f.facilityId}`) +
              ` (${f.facId ?? f.id ?? f.facilityId})`,
            raw: f,
          }))
        : Array.isArray(hookFacilities) && hookFacilities.length
        ? hookFacilities.map((f) => ({
            id: f.facId ?? f.id ?? f.facilityId,
            value: String(f.facId ?? f.id ?? f.facilityId),
            label:
              (f.facilityName || f.name || f.facName || f.description || `Facility ${f.facId ?? f.id ?? f.facilityId}`) +
              ` (${f.facId ?? f.id ?? f.facilityId})`,
            raw: f,
          }))
        : [];

    // Note types
    let ntOpts =
      Array.isArray(cachedNoteTypeOptions) && cachedNoteTypeOptions.length
        ? cachedNoteTypeOptions
        : Array.isArray(hookNoteTypeOptions) && hookNoteTypeOptions.length
        ? hookNoteTypeOptions
        : Array.isArray(cachedNoteTypes) && cachedNoteTypes.length
        ? // Fallback has only labels; no ids/facId to filter by
          cachedNoteTypes.map((label) => ({
            id: null,
            value: String(label),
            label: String(label),
            facId: null,
          }))
        : Array.isArray(hookNoteTypes) && hookNoteTypes.length
        ? hookNoteTypes.map((label) => ({
            id: null,
            value: String(label),
            label: String(label),
            facId: null,
          }))
        : [];

    setFacilitiesOptions(facOpts);
    setNoteTypeOptions(ntOpts);

    // Initialize selections (do NOT force first; prefer soft default or keep empty)
    const savedFacId = sessionStorage.getItem("pcc_facId") || defaultFacId || "";
    if (savedFacId) {
      const exists = facOpts.some((o) => o.value === String(savedFacId));
      setSelectedFacilityId(exists ? String(savedFacId) : "");
    } else {
      setSelectedFacilityId("");
    }

    // Do not auto-pick a note type; wait for user
    setSelectedNoteTypeId("");
  }, [hookFacilitiesOptions, hookNoteTypeOptions, hookFacilities, hookNoteTypes, defaultFacId, sessionId]);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await client.graphql({
          query: getSessionAnalysis,
          variables: { id: sessionId },
        });
        const sessionRecord = response.data.getSessionAnalysis || {};
        const analysis = sessionRecord?.analysisResult;
        const parsed = analysis ? JSON.parse(analysis) : {};
        const populated = { ...parsed };

        if (populated.noteTemplate !== "BEHAVIOR_THERAPY") {
          for (const key in DEFAULT_REVIEW_OF_SYSTEMS) {
            if (!populated[key]) {
              populated[key] = DEFAULT_REVIEW_OF_SYSTEMS[key];
            }
          }
        }

        // Keep parsed narrative fields, but never let them override authoritative signing metadata.
        setResult({
          ...populated,
          ...sessionRecord,
          isSigned: Boolean(sessionRecord?.isSigned),
          signedBy: sessionRecord?.signedBy || null,
          signedAt: sessionRecord?.signedAt || null,
        });
        setSelectedPlaceOfService(String(populated.placeOfService || ""));
        setEditedResult(populated);
      } catch (err) {
        console.error("Error fetching analysis:", err);
        setError("Failed to load analysis.");
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) fetchResult();
  }, [sessionId]);

  const handleChange = (field, value) => {
    setEditedResult((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggle = (field) => {
    setEditedResult((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleDiagnosisChange = (index, codeOrDesc, value) => {
    const newDiagnoses = [...(editedResult.diagnoses || [])];
    newDiagnoses[index] = { ...newDiagnoses[index], [codeOrDesc]: value };
    setEditedResult((prev) => ({ ...prev, diagnoses: newDiagnoses }));
  };

  const removeDiagnosis = (index) => {
    const newDiagnoses = [...(editedResult.diagnoses || [])];
    newDiagnoses.splice(index, 1);
    setEditedResult((prev) => ({ ...prev, diagnoses: newDiagnoses }));
  };

  const addDiagnosis = () => {
    const diagnoses = editedResult.diagnoses || [];
    theHasEmpty: {
      const hasEmpty = diagnoses.some((d) => !d.code && !d.description);
      if (diagnoses.length >= 12 || hasEmpty) return;
    }
    const updated = [...diagnoses, { code: "", description: "" }];
    setEditedResult((prev) => ({ ...prev, diagnoses: updated }));
  };

  const persistChanges = async () => {
    await client.graphql({
      query: updateSessionAnalysis,
      variables: {
        input: {
          id: sessionId,
          analysisResult: JSON.stringify(editedResult),
          timeIn: editedResult.timeIn || null,
          timeOut: editedResult.timeOut || null,
          durationMinutes:
            editedResult.durationMinutes != null ? parseInt(editedResult.durationMinutes) : null,
          isManualTimeEntry: !!editedResult.isManualTimeEntry,
        },
      },
    });
  };

  const handleSave = async () => {
    try {
      await persistChanges();
      alert("Changes saved successfully ✅");
    } catch (err) {
      console.error("Failed to save analysis:", err);
      alert("Failed to save changes ❌");
    }
  };

  // Ensure PCC clinical data / other edits are persisted before the print view re-reads from the DB
  const handlePrint = async () => {
    try {
      await persistChanges();
    } catch (err) {
      console.error("Failed to save analysis before printing:", err);
      alert("⚠️ Could not save latest changes; the printed note may be out of date.");
    }
    navigate(`/session/${sessionId}/print`);
  };

  // 🆕 when a patient is picked from the type-ahead
  const handlePatientPicked = async (p) => {
    const patientId = p?.patientId ? String(p.patientId) : "";
    const fetchId = activePatientFetchRef.current + 1;
    activePatientFetchRef.current = fetchId;

    setPatientClinicalDataError("");
    setPatientClinicalDataWarning("");
    setEditedResult((prev) => ({
      ...prev,
      patientId,
      name: p?.name || prev.name,
      dateOfBirth: p?.dob || prev.dateOfBirth,
      mrn: p?.mrn || prev.mrn,
      pccMedications: "",
      pccConditionsProblems: "",
      pccDiagnosticReports: "",
      pccMedicationsSnapshot: [],
      pccConditionsSnapshot: [],
      pccDiagnosticReportsSnapshot: [],
      pccClinicalDataSyncedAt: null,
    }));

    if (!hasPCCSession || !patientId || quotaExceeded) {
      return;
    }

    setLoadingPatientClinicalData(true);
    try {
      await ensureMinPccGap();

      const resp = await post({
        apiName: "pccapi",
        path: "/pushToPCC",
        options: {
          body: {
            action: "patientClinicalData",
            orgUuid,
            pccAccessToken: token,
            pccUserId,
            facilityId: selectedFacilityId ? Number(selectedFacilityId) : undefined,
            patientId,
          },
          headers: {
            "X-PCC-OrgUuid": orgUuid,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      }).response;

      const raw = await resp.body.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (activePatientFetchRef.current !== fetchId) {
        return;
      }

      if (resp.statusCode < 200 || resp.statusCode >= 300) {
        throw new Error(data?.error || raw || "Failed to load patient clinical data.");
      }

      setEditedResult((prev) => ({
        ...prev,
        pccMedications: data?.medicationsText || "",
        pccConditionsProblems: data?.conditionsText || "",
        pccDiagnosticReports: data?.diagnosticReportsText || "",
        pccMedicationsSnapshot: data?.medications || prev.pccMedicationsSnapshot || [],
        pccConditionsSnapshot: data?.conditions || prev.pccConditionsSnapshot || [],
        pccDiagnosticReportsSnapshot:
          data?.diagnosticReports || prev.pccDiagnosticReportsSnapshot || [],
        pccClinicalDataSyncedAt: new Date().toISOString(),
      }));

      const resourceErrors = Array.isArray(data?.clinicalDataErrors)
        ? data.clinicalDataErrors
        : [];
      if (resourceErrors.length) {
        setPatientClinicalDataWarning(
          `PCC could not load: ${resourceErrors.map((item) => item.resource).join(", ")}.`
        );
      }
    } catch (err) {
      if (activePatientFetchRef.current === fetchId) {
        setPatientClinicalDataError(
          "Could not load PCC medications, conditions/problems, or diagnostic reports for this patient."
        );
        console.warn("PCC patient clinical data fetch failed:", err);
      }
    } finally {
      if (activePatientFetchRef.current === fetchId) {
        setLoadingPatientClinicalData(false);
      }
    }
  };

  const handlePlaceOfServiceChange = (value) => {
    setSelectedPlaceOfService(value);
    setEditedResult((prev) => ({
      ...prev,
      placeOfService: value || null,
    }));
  };

  // 🆕 PCC → DB patient sync for current facility
  async function syncPatientsForFacility(facId) {
    if (!orgUuid || !token || !pccUserId || !facId) return;

    // If quota already exceeded, avoid calling PCC
    if (quotaExceeded) {
      alert("PCC quota reached, try again later.");
      return;
    }

    setSyncingPatients(true);
    try {
      console.log("▶️ syncPatientsForFacility", {
        orgUuid,
        pccUserId,
        facId: Number(facId),
      });

      // Enforce 20ms gap before PCC call
      await ensureMinPccGap();

      const resp = await post({
        apiName: "pccapi",
        path: "/pushToPCC",
        options: {
          body: {
            action: "syncPatients",
            orgUuid,
            pccAccessToken: token,
            pccUserId, // required by the sync action
            facilityId: Number(facId),
          },
          headers: {
            "X-PCC-OrgUuid": orgUuid,
            "X-PCC-UserId": pccUserId,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      }).response;

      const status = resp?.statusCode;
      const text = await resp?.body?.text();
      console.log("◀️ syncPatients response", { status, text });
    } catch (e) {
      console.error("syncPatients failed", e);
    } finally {
      setSyncingPatients(false);
    }
  }

  const handlePushToPCC = async () => {
    try {
      if (!hasPCCSession) {
        alert("⚠️ Not signed in with PCC.");
        return;
      }

      if (quotaExceeded) {
        alert("PCC quota reached, try again later.");
        return;
      }

      // 🆕 require patient first
      if (!editedResult?.patientId) {
        alert("Please select a patient first.");
        return;
      }
      if (!selectedFacilityId) {
        alert("Please select a facility.");
        return;
      }
      if (!selectedNoteTypeId) {
        alert("Please select a note type.");
        return;
      }

      // Persist chosen facility for next load
      sessionStorage.setItem("pcc_facId", String(selectedFacilityId));

      // Map chosen note type → id (if available)
      const chosenNote = noteTypeOptions.find((o) => o.value === selectedNoteTypeId) || null;

      const facilityIdNum = Number(selectedFacilityId); // PCC expects number
      const progressNoteTypeIdNum =
        chosenNote && chosenNote.id != null ? Number(chosenNote.id) : null;
      const noteTypeLabel = chosenNote ? chosenNote.label : selectedNoteTypeId; // fallback label

      console.log("🧭 PushToPCC inputs:", {
        orgUuid,
        tokenLen: token ? token.length : 0,
        facilityIdNum,
        progressNoteTypeIdNum,
        noteTypeLabel,
      });

      // Enforce 20ms gap before PCC call
      await ensureMinPccGap();

      const resp = await post({
        apiName: "pccapi",
        path: `/pushToPCC?sessionId=${encodeURIComponent(
          sessionId
        )}&userId=${encodeURIComponent(pccUserId)}`,
        options: {
          body: {
            orgUuid,
            pccAccessToken: token,
            facilityId: facilityIdNum,
            // Prefer ID; keep label for back-compat
            progressNoteTypeId: progressNoteTypeIdNum,
            noteType: noteTypeLabel,
            // providerName: editedResult?.provider || result?.signedBy || undefined,
          },
          headers: {
            "X-PCC-OrgUuid": orgUuid,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      }).response;

      const statusCode = resp?.statusCode ?? 0;
      const raw = await resp.body.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch {
        /* leave null if not JSON */
      }

      if (statusCode < 200 || statusCode >= 300) {
        console.error("PCC push failed:", statusCode, raw);
        alert(`❌ Failed to push note to PCC.\n${raw}`);
        return;
      }

      // Success: capture PCC Note ID if present
      const progressNoteId = data?.progressNoteId ?? data?.id ?? data?.noteId ?? null;
      console.log("✅ Pushed to PCC. progressNoteId:", progressNoteId, "raw:", raw);

      // Persist the PCC id into your analysisResult JSON (back-compat path)
      try {
        const updated = {
          ...editedResult,
          pccProgressNoteId: progressNoteId,
          pccPushedAt: new Date().toISOString(),
          pccNoteType: noteTypeLabel,
          pccFacilityId: String(selectedFacilityId),
        };

        await client.graphql({
          query: updateSessionAnalysis,
          variables: {
            input: {
              id: sessionId,
              analysisResult: JSON.stringify(updated),
            },
          },
        });

        setEditedResult(updated);
      } catch (persistErr) {
        console.warn(
          "⚠️ Saved to PointClickCare, but couldn’t persist progressNoteId to DB:",
          persistErr
        );
      }

      // Cache in sessionStorage (session-scoped) too
      if (progressNoteId) {
        sessionStorage.setItem(PCC_ID_KEY, String(progressNoteId));
      }

      alert(
        progressNoteId
          ? `✅ Successfully pushed note to PointClickCare (Note #${progressNoteId})`
          : "✅ Successfully pushed note to PointClickCare"
      );
    } catch (err) {
      console.error("Error pushing note to PointClickCare:", err);
      alert("❌ Failed to push note to PointClickCare.");
    }
  };

  // ------------ Strike out note in PCC ------------
  const handleStrikeOutInPCC = async () => {
    try {
      if (!hasPCCSession) {
        alert("⚠️ Not signed in with PointClickCare.");
        return;
      }
      if (quotaExceeded) {
        alert("PCC quota reached, try again later.");
        return;
      }
      if (!resolvedPccNoteId) {
        alert("No PCC note found on this session.");
        return;
      }
      if (!selectedFacilityId) {
        alert("Select the facility used to push.");
        return;
      }

      const reason = window.prompt(
        "Provide a reason for striking out this note (required by PCC):",
        "Entered in error"
      );
      if (reason == null || !reason.trim()) return; // user cancelled

      if (!window.confirm("Strike out this note in PCC? This cannot be undone.")) return;

      // Enforce 20ms gap before PCC call
      await ensureMinPccGap();

      const resp = await post({
        apiName: "pccapi",
        path: `/pushToPCC?sessionId=${encodeURIComponent(
          sessionId
        )}&userId=${encodeURIComponent(pccUserId)}`,
        options: {
          body: {
            action: "strikeout", // 👈 new action (Lambda handles it)
            orgUuid,
            pccAccessToken: token,
            facilityId: Number(selectedFacilityId),
            pccNoteId: resolvedPccNoteId,
            strikeReason: reason.trim(), // passed through to Lambda
          },
          headers: {
            "X-PCC-OrgUuid": orgUuid,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      }).response;

      const statusCode = resp?.statusCode ?? 0;
      const raw = await resp.body.text();

      // Treat 200/204 as success (depends on tenant behavior)
      if (statusCode === 200 || statusCode === 204) {
        alert("🚫 Note struck out in PointClickCare.");
        return;
      }

      console.error("PointClickCare strike out failed:", statusCode, raw);
      alert(`❌ Failed to strike out PointClickCare note.\n${raw}`);
    } catch (err) {
      console.error("Error striking out PointClickCare note:", err);
      alert("❌ Failed to strike out PointClickCare note.");
    }
  };

  const handlePCCSignOut = () => {
    sessionStorage.removeItem("pccSession");
    sessionStorage.removeItem("pcc_accessToken");
    sessionStorage.removeItem("pcc_refreshToken");
    sessionStorage.removeItem("pcc_orgUuid");
    sessionStorage.removeItem("pcc_userId");
    sessionStorage.removeItem("pcc_orgId");
    sessionStorage.removeItem("pcc_appEndUser");
    sessionStorage.removeItem("pcc_facId");
    sessionStorage.removeItem("pcc_defaultFacId");
    window.location.reload();
  };

  const formatDate = (isoDate) => {
    try {
      const d = new Date(isoDate);
      return `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d
        .getDate()
        .toString()
        .padStart(2, "0")}/${d.getFullYear()}`;
    } catch {
      return isoDate;
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatServiceDateTimeReference = () => {
    const serviceDate = createdAt ? formatDate(createdAt) : "";
    const timeInText = formatTime(editedResult.timeIn || result.timeIn);
    const timeOutText = formatTime(editedResult.timeOut || result.timeOut);

    if (timeInText && timeOutText) return `${serviceDate} ${timeInText} - ${timeOutText}`.trim();
    if (timeInText) return `${serviceDate} ${timeInText}`.trim();
    if (timeOutText) return `${serviceDate} ${timeOutText}`.trim();
    return serviceDate || new Date().toLocaleString();
  };

  const formatScalar = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "";
  };

  const buildCodingNoteText = () => {
    const lines = [];
    const patientName = formatScalar(editedResult.name || result.name) || "Unknown Patient";
    const patientId = formatScalar(editedResult.patientId || result.patientId) || "Unknown ID";
    const dateOfBirth = formatScalar(editedResult.dateOfBirth || result.dateOfBirth);
    const placeOfService = formatScalar(selectedPlaceOfService || editedResult.placeOfService);

    lines.push("Session Note for Coding Review");
    lines.push(`Patient Name: ${patientName}`);
    lines.push(`Patient ID: ${patientId}`);
    if (dateOfBirth) lines.push(`Date of Birth: ${dateOfBirth}`);
    if (placeOfService) lines.push(`Place of Service: ${placeOfService}`);
    lines.push(`Date/Time of Service: ${formatServiceDateTimeReference()}`);
    lines.push("");

    const sectionFieldMap = isBehaviorTherapy
      ? [
          ["Presenting Problem", editedResult.presentingProblem || result.presentingProblem],
          ["Past Medical History", editedResult.pastMedicalHistory || result.pastMedicalHistory],
          ["Family History", editedResult.familyHistory || result.familyHistory],
          ["Medications", editedResult.medications || result.medications],
          ["PCC Medications", editedResult.pccMedications || result.pccMedications],
          ["PCC Conditions / Problems", editedResult.pccConditionsProblems || result.pccConditionsProblems],
          ["PCC Diagnostic Reports", editedResult.pccDiagnosticReports || result.pccDiagnosticReports],
          ["Allergies", editedResult.allergies || result.allergies],
          ["Mental Status Exam", editedResult.mentalStatusExam || result.mentalStatusExam],
          ["Risk Assessment", editedResult.riskAssessment || result.riskAssessment],
          ["Therapy Modality / Interventions Used", editedResult.therapyModality || result.therapyModality],
          ["Patient Response to Treatment", editedResult.patientResponse || result.patientResponse],
          ["Progress Toward Goals", editedResult.progressTowardGoals || result.progressTowardGoals],
          ["Homework / Between-Session Plan", editedResult.homeworkPlan || result.homeworkPlan],
          ["Treatment Plan", editedResult.treatmentPlan || result.treatmentPlan],
          ["Verbal Consent", editedResult.verbalConsent || result.verbalConsent],
          ["Occupation", editedResult.occupation || result.occupation],
          ["Alcohol", editedResult.alcohol || result.alcohol],
          ["Drugs", editedResult.drugs || result.drugs],
          ["Smoking", editedResult.smoking || result.smoking],
          ["SDOH", editedResult.sdoh || result.sdoh],
        ]
      : [
          ["History of Present Illness", editedResult.historyPresentIllness || result.historyPresentIllness],
          ["Past Medical History", editedResult.pastMedicalHistory || result.pastMedicalHistory],
          ["Family History", editedResult.familyHistory || result.familyHistory],
          ["Medications", editedResult.medications || result.medications],
          ["Conditions / Problems", editedResult.conditions || result.conditions],
          ["PCC Medications", editedResult.pccMedications || result.pccMedications],
          ["PCC Conditions / Problems", editedResult.pccConditionsProblems || result.pccConditionsProblems],
          ["PCC Diagnostic Reports", editedResult.pccDiagnosticReports || result.pccDiagnosticReports],
          ["Allergies", editedResult.allergies || result.allergies],
          ["Physical Exam", editedResult.physicalExam || result.physicalExam],
          ["Procedures", editedResult.procedures || result.procedures],
          ["Assessment and Plan", editedResult.planAssessment || result.planAssessment],
          ["Verbal Consent", editedResult.verbalConsent || result.verbalConsent],
          ["Occupation", editedResult.occupation || result.occupation],
          ["Alcohol", editedResult.alcohol || result.alcohol],
          ["Drugs", editedResult.drugs || result.drugs],
          ["Smoking", editedResult.smoking || result.smoking],
          ["SDOH", editedResult.sdoh || result.sdoh],
        ];

    sectionFieldMap.forEach(([label, value]) => {
      const text = formatScalar(value);
      if (text) {
        lines.push(`${label}:`);
        lines.push(text);
        lines.push("");
      }
    });

    const diagnoses = Array.isArray(editedResult.diagnoses) ? editedResult.diagnoses : [];
    if (diagnoses.length) {
      lines.push("Diagnoses (ICD-10):");
      diagnoses.forEach((d, idx) => {
        const code = formatScalar(d?.code);
        const desc = formatScalar(d?.description);
        if (code || desc) {
          lines.push(`${idx + 1}. ${code || "No Code"} - ${desc || "No Description"}`);
        }
      });
      lines.push("");
    }

    if (!isBehaviorTherapy) {
      lines.push("Review of Systems:");
      Object.entries(SYSTEM_LABELS).forEach(([key, label]) => {
        const value = formatScalar(editedResult[key] || result[key]);
        if (value) {
          lines.push(`${label}: ${value}`);
        }
      });
    }

    const addendums = Array.isArray(result.addendums) ? result.addendums : [];
    if (addendums.length) {
      lines.push("");
      lines.push("Addendums:");
      addendums.forEach((a, idx) => {
        const by = formatScalar(a?.addedBy) || "Unknown";
        const at = a?.addedAt ? new Date(a.addedAt).toLocaleString() : "Unknown time";
        const text = formatScalar(a?.text) || "(No addendum text)";
        lines.push(`${idx + 1}. ${by} at ${at}`);
        lines.push(text);
      });
    }

    return lines.join("\n").trim();
  };

  const handleCodeMe = () => {
    if (!selectedPlaceOfService) {
      alert("Please select Place of Service before coding analysis.");
      return;
    }

    const patientName = formatScalar(editedResult.name || result.name) || "Unknown Patient";
    const patientId = formatScalar(editedResult.patientId || result.patientId) || "Unknown ID";
    const caseName = `${patientName} - ${patientId}`;
    const patientReference = formatServiceDateTimeReference();

    const safeName = patientName.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "patient";
    const safeId = patientId.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "id";
    const generatedFileName = `${safeName}_${safeId}_session_note.txt`;

    navigate("/coding-review", {
      state: {
        source: "session-note",
        sourceSessionId: sessionId,
        autoSubmit: true,
        prefill: {
          caseName,
          patientReference,
          placeOfService: selectedPlaceOfService,
          noteTemplate: isBehaviorTherapy ? "BEHAVIOR_THERAPY" : "STANDARD_MEDICAL",
        },
        generatedDocuments: [
          {
            name: generatedFileName,
            type: "text/plain",
            content: buildCodingNoteText(),
          },
        ],
      },
    });
  };

  const renderEditableField = (label, field, overrideValue = null) => {
    let value = overrideValue !== null ? overrideValue : editedResult[field] || "";
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      value = Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n");
    }

    return (
      <div className="field-group">
        <label className="field-label">{label}</label>
        <textarea
          value={value}
          onChange={(e) => handleChange(field, e.target.value)}
          className="field-input"
          disabled={result?.isSigned}
          rows={3}
        />
      </div>
    );
  };

  const renderDiagnoses = () => {
    const diagnoses = editedResult.diagnoses || [];
    return (
      <section className="note-section">
        <h3>Diagnoses (ICD-10)</h3>
        {diagnoses.map((d, i) => (
          <div key={i} className="field-group">
            <label className="field-label">Diagnosis {i + 1}</label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
              <input
                type="text"
                className="field-input"
                placeholder="ICD-10 Code"
                value={d.code}
                disabled={result?.isSigned}
                onChange={(e) => handleDiagnosisChange(i, "code", e.target.value)}
              />
              <textarea
                className="field-input"
                placeholder="Description"
                value={d.description}
                disabled={result?.isSigned}
                onChange={(e) => handleDiagnosisChange(i, "description", e.target.value)}
              />
              {!result?.isSigned && (
                <button
                  type="button"
                  onClick={() => removeDiagnosis(i)}
                  style={{ padding: "0.25rem 0.5rem" }}
                >
                  ❌
                </button>
              )}
            </div>
          </div>
        ))}
        {!result?.isSigned && diagnoses.length < 12 && (
          <button type="button" className="button add" onClick={addDiagnosis}>
            ➕ Add Additional Dx
          </button>
        )}
      </section>
    );
  };

  const renderReadOnlyField = (label, value) => {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      value = Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n");
    }
    return (
      <div className="field-group">
        <label className="field-label">{label}</label>
        <div className="text-muted">{value || "N/A"}</div>
      </div>
    );
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-danger">{error}</p>;
  if (!result) return <p>No analysis available.</p>;

  // Filter note types by facility if we have facId on options; otherwise show all
  const facFiltered =
    selectedFacilityId && noteTypeOptions.some((o) => o.facId != null)
      ? noteTypeOptions.filter((o) => String(o.facId) === String(selectedFacilityId))
      : noteTypeOptions;

  // 🆕 Graceful fallback to "all" when no facility-specific types match
  const filteredNoteTypeOptions = facFiltered.length ? facFiltered : noteTypeOptions;

  // 🆕 Only disable note type select if no facility is selected or after a real push
  const noteTypeDisabled = !selectedFacilityId || hasPushedToPCC;

  return (
    <div className="container">
      <div className="toolbar">
        <h2 className="session-toolbar-title">
          <span className="session-name">{result.name || "Unknown Name"}</span>
          <span className="session-separator"> - </span>
          <span className="session-patient-id">{result.patientId || "No Patient ID"}</span>
          {createdAt ? <span className="session-date"> ({formatDate(createdAt)})</span> : ""}
        </h2>
        <button
          type="button"
          className="session-toolbar-toggle"
          aria-expanded={isActionMenuOpen}
          aria-controls={`session-actions-${sessionId}`}
          onClick={() => setIsActionMenuOpen((open) => !open)}
        >
          <span aria-hidden="true">☰</span>
          <span>{isActionMenuOpen ? "Close note actions" : "Note actions"}</span>
        </button>
        <div
          id={`session-actions-${sessionId}`}
          className={`session-toolbar-actions${isActionMenuOpen ? " is-open" : ""}`}
        >
          <label className="session-toolbar-pos" htmlFor={`pos-select-${sessionId}`}>
            <span>Place of Service</span>
            <select
              id={`pos-select-${sessionId}`}
              value={selectedPlaceOfService}
              onChange={(e) => handlePlaceOfServiceChange(e.target.value)}
              disabled={!result?.isSigned}
            >
              <option value="">Select...</option>
              {PLACE_OF_SERVICE_OPTIONS.map((pos) => (
                <option key={pos} value={pos}>
                  {pos}
                </option>
              ))}
            </select>
          </label>

          <div className="buttons note-actions-grid">
          <SignNoteButton
            sessionId={sessionId}
            isSigned={result?.isSigned}
            onSigned={() => {
              setResult((prev) => ({ ...prev, isSigned: true }));
            }}
            className="button save note-action-button"
            signedClassName="button note-action-button note-action-signed"
          />
          {result?.isSigned && (
            <button onClick={handleCodeMe} className="button save note-action-button">
              Code Me
            </button>
          )}
          <button
            onClick={handlePrint}
            className="button print note-action-button"
          >
            Print PDF
          </button>
          <SendToEBridgeButton
            documentType="SIGNED_NOTE"
            sessionId={sessionId}
            enabled={Boolean(result?.isSigned)}
            className="button save note-action-button"
          />
          <button onClick={handleSave} className="button save note-action-button">
            Save Changes
          </button>
          </div>
        </div>
      </div>

      <div className="note-body">
        {hasPCCSession && (
          <section className="note-section">
            <h3>Push to PointClickCare</h3>
            <div className="field-group">
              <div className="field-label text-muted">
                {quotaExceeded
                  ? "⛔ PCC quota reached, calls are temporarily disabled."
                  : "✅ PointClickCare Session Active"}
              </div>
              <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                <label>
                  Facility:&nbsp;
                  <select
                    value={selectedFacilityId}
                    onChange={async (e) => {
                      const v = e.target.value; // string
                      setSelectedFacilityId(v);
                      setSelectedNoteTypeId(""); // clear when facility changes
                      sessionStorage.setItem("pcc_facId", String(v));
                      // 🆕 trigger PCC→DB patient sync for this facility
                      if (!quotaExceeded) {
                        syncPatientsForFacility(v);
                      }
                    }}
                    disabled={facilitiesOptions.length === 0 || quotaExceeded}
                  >
                    <option value="" disabled>
                      Select a facility…
                    </option>
                    {facilitiesOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                {syncingPatients && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Syncing patients…
                  </span>
                )}
                <label>
                  Note Type:&nbsp;
                  <select
                    value={selectedNoteTypeId}
                    onChange={(e) => setSelectedNoteTypeId(e.target.value)}
                    // 🆕 Never disable just because the filtered list is empty; we fall back to "all"
                    disabled={noteTypeDisabled || quotaExceeded}
                  >
                    <option value="" disabled>
                      Select a note type…
                    </option>
                    {filteredNoteTypeOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>

                {/* Main action flips between Push and Delete */}
                <button
                  onClick={hasPushedToPCC ? handleStrikeOutInPCC : handlePushToPCC}
                  className={`button ${hasPushedToPCC ? "danger" : "print"}`}
                  disabled={quotaExceeded}
                >
                  {quotaExceeded
                    ? "PCC Quota Reached"
                    : hasPushedToPCC
                    ? "🚫 Strike Out in PointClickCare"
                    : "📤 Push Note to PointClickCare"}
                </button>

                <button onClick={handlePCCSignOut} className="button danger">
                  🚪 Sign Out of PointClickCare
                </button>
              </div>

              {/* 🆕 Hint when we fell back to "all" note types */}
              {selectedFacilityId &&
                noteTypeOptions.some((o) => o.facId != null) &&
                !facFiltered.length && (
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    No facility-specific note types; showing all.
                  </div>
                )}
            </div>
          </section>
        )}

        <section className="note-section">
          <h3>Patient Information</h3>
          {renderEditableField("Name", "name")}
          {/* 🆕 Replace manual Patient ID with the type-ahead */}
          <div className="field-group">
            <label className="field-label">Patient (search)</label>
            <PatientSelectTypeahead
              orgUuid={orgUuid}
              pccUserId={pccUserId}
              selectedFacilityId={selectedFacilityId}
              onSelect={handlePatientPicked}
              disabled={result?.isSigned}
            />
            {editedResult?.patientId ? (
              <div className="text-muted" style={{ marginTop: 6 }}>
                Selected: <strong>{editedResult.name || "Unknown"}</strong>
                {editedResult.mrn ? ` — MRN ${editedResult.mrn}` : ""}
                {editedResult.dateOfBirth ? ` — DOB ${editedResult.dateOfBirth}` : ""}
              </div>
            ) : (
              <div className="text-muted" style={{ marginTop: 6 }}>
                No patient selected yet.
              </div>
            )}
            {loadingPatientClinicalData && (
              <div className="text-muted" style={{ marginTop: 6 }}>
                Loading PCC medications, conditions/problems, and diagnostic reports…
              </div>
            )}
            {patientClinicalDataError && (
              <div className="text-danger" style={{ marginTop: 6 }}>
                {patientClinicalDataError}
              </div>
            )}
            {patientClinicalDataWarning && (
              <div className="text-muted" style={{ marginTop: 6 }}>
                {patientClinicalDataWarning}
              </div>
            )}
          </div>
          {/* (Keep DOB editable if you want, it will autofill on pick) */}
          {renderEditableField("Date of Birth", "dateOfBirth")}
          {renderEditableField("Age", "age")}
          {renderReadOnlyField("Date of Service", createdAt ? formatDate(createdAt) : "")}
          {renderReadOnlyField("Time In", formatTime(result.timeIn))}
          {renderReadOnlyField("Time Out", formatTime(result.timeOut))}
          {renderEditableField("Duration (min)", "durationMinutes")}
          <div className="field-group">
            <label className="field-label">Manual Time Entry</label>
            <input
              type="checkbox"
              checked={!!editedResult.isManualTimeEntry}
              onChange={() => handleToggle("isManualTimeEntry")}
              disabled={result?.isSigned}
            />
          </div>
          {renderEditableField("Verbal Consent", "verbalConsent")}
        </section>

        {hasPCCSession && (
          <section className="note-section">
            <h3>PointClickCare Clinical Data</h3>
            {renderEditableField("PCC Medications", "pccMedications")}
            {renderEditableField("PCC Conditions / Problems", "pccConditionsProblems")}
            {renderEditableField("PCC Diagnostic Reports", "pccDiagnosticReports")}
          </section>
        )}

        <section className="note-section">
          <h3>Social History</h3>
          {renderEditableField("Occupation", "occupation")}
          {renderEditableField("Alcohol", "alcohol")}
          {renderEditableField("Drugs", "drugs")}
          {renderEditableField("Smoking", "smoking")}
          {renderEditableField("SDOH", "sdoh")}
        </section>

        {isBehaviorTherapy ? (
          <section className="note-section">
            <h3>Behavior Therapy Note</h3>
            {renderEditableField("Presenting Problem", "presentingProblem")}
            {renderEditableField("Past Medical History", "pastMedicalHistory")}
            {renderEditableField("Family History", "familyHistory")}
            {renderEditableField("Medications", "medications")}
            {renderEditableField("Allergies", "allergies")}
            {renderEditableField("Mental Status Exam", "mentalStatusExam")}
            {renderEditableField("Risk Assessment", "riskAssessment")}
            {renderEditableField("Therapy Modality / Interventions Used", "therapyModality")}
            {renderEditableField("Patient Response to Treatment", "patientResponse")}
            {renderEditableField("Progress Toward Goals", "progressTowardGoals")}
            {renderEditableField("Homework / Between-Session Plan", "homeworkPlan")}
            {renderEditableField("Treatment Plan", "treatmentPlan")}
          </section>
        ) : (
          <section className="note-section">
            <h3>Medical Details</h3>
            {renderEditableField("History of Present Illness", "historyPresentIllness")}
            {renderEditableField("Past Medical History", "pastMedicalHistory")}
            {renderEditableField("Family History", "familyHistory")}
            {renderEditableField("Medications", "medications")}
            {renderEditableField("Allergies", "allergies")}
            {renderEditableField("Physical Exam", "physicalExam")}
            {renderEditableField("Procedures", "procedures")}
            {renderEditableField("Assessment & Plan", "planAssessment")}
          </section>
        )}

        {renderDiagnoses()}

        {!isBehaviorTherapy && (
          <section className="note-section">
            <h3>Review of Systems</h3>
            {Object.entries(SYSTEM_LABELS).map(([key, label]) => renderEditableField(label, key))}
          </section>
        )}

        {result?.isSigned && (
          <section className="note-section">
            <h3>Add Addendum</h3>
            <AddAddendum
              sessionId={sessionId}
              orgUuid={orgUuid}
              token={token}
              pccNoteId={resolvedPccNoteId}
              facilityId={selectedFacilityId}
              hasPCCSession={hasPCCSession}
              providerName={editedResult?.provider || result?.signedBy || "Provider"}
              onAdded={(newAddendum) => {
                setResult((prev) => ({
                  ...prev,
                  addendums: [...(prev.addendums || []), newAddendum],
                }));
              }}
            />
          </section>
        )}

        {result?.addendums?.length > 0 && (
          <section className="note-section">
            <h3>Previous Addendums</h3>
            {result.addendums.map((a) => (
              <div key={a.addedAt + a.addedBy} className="field-group">
                <label className="field-label">
                  {a.addedBy} — {new Date(a.addedAt).toLocaleString()}
                </label>
                <div className="text-muted">{a.text}</div>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}


